import React, { useState } from "react";
import { Table, Button } from "reactstrap";
import formatCurrency from "constants/formatCurrency";
import TablePaginationButtons from "./TablePaginationButtons";
import SortableTableHeader from "./SortableTableHeader";
import monthlyParkingTotalCalculations from "constants/monthlyParkingTotalCalculations";
import CreateOrderTableCells from "pages/MonthlyParking/CreateOrderTableCells";
import ExportButton from "./ExportButton";
import formatPercentage from "constants/formatPercentage";

const MonthlyOrdersTableByLocationView = ({
    orders,
    search,
    filters,
    filterOrders,
    expandedLocation,
    setExpandedLocation,
    locationPages,
    setLocationPages,
    pageSize,
    setSortConfig,
    sortedOrders,
    onRefresh
}) => {
    const user = JSON.parse(localStorage.getItem("authUser"));
    const [hideZeros, setHideZeros] = useState(false);

    const filteredAndSortedLocations = orders
        .filter((loc) => {
            const q = search.trim().toLowerCase();
            if (!q) return true;

            const locationMatch = (loc.name || "").toLowerCase().includes(q);
            const orderMatch = (loc.orders || []).some((o) =>
                ((o.customer_first_name + " " + o.customer_last_name) || "").toLowerCase().includes(q) ||
                (o.customer_email || "").toLowerCase().includes(q) ||
                (o.customer_phone || "").toLowerCase().includes(q) ||
                (o.license_plates || "").toLowerCase().includes(q) ||
                (o.car_models || "").toLowerCase().includes(q)
            );

            return locationMatch || orderMatch;
        })
        .map((loc) => {
            const filteredOrders = filterOrders(
                (loc.orders || []).map((o) => ({ ...o, locationName: loc.name }))
            );

            const { totalCash, totalCheque, totalStripe, totalQbo, totalUnknown, totalCollected } =
                monthlyParkingTotalCalculations(filteredOrders);

            return {
                ...loc,
                filteredOrders,
                totalCash,
                totalCheque,
                totalStripe,
                totalQbo,
                totalUnknown,
                totalCollected,
            };
        });

    const sortedLocations = sortedOrders(filteredAndSortedLocations).filter((loc) => {
        if (!hideZeros) return true;
        const { totalCash, totalCheque, totalStripe, totalQbo, totalUnknown } =
            monthlyParkingTotalCalculations(loc.filteredOrders);
        return totalCash + totalCheque + totalStripe + totalQbo + totalUnknown > 0;
    });

    return (
        <>
            <Table className="align-middle table-hover table-nowrap mb-0" responsive>
                <SortableTableHeader
                    columns={[
                        { label: "Location" },
                        { label: "Total Cash", sortKey: "totalCash" },
                        { label: "Total Check", sortKey: "totalCheque" },
                        { label: "Total Stripe", sortKey: "totalStripe" },
                        { label: "Total QBO", sortKey: "totalQbo" },
                        { label: "Total Unknown", sortKey: "totalUnknown" },
                        { label: "Total Collected", sortKey: "totalCollected" },
                        { label: "Used Spaces", sortKey: "usedSpaces" },
                        { label: "Number of Spaces", sortKey: "numberOfSpaces" },
                        { label: "Utilization Rate", sortKey: "utilizationRate" },
                        { label: "View Orders" },
                        { label: "Export" }
                    ]}
                    onSort={setSortConfig}
                    showHideZero={true}
                    hideZeros={hideZeros}
                    onToggleHideZero={() => setHideZeros(!hideZeros)}
                    hideZeroText={"Hide Zero Orders"}
                />
                <tbody>
                    {sortedLocations.map((loc) => {
                        const { totalCash, totalCheque, totalStripe, totalQbo, totalUnknown, totalCollected } =
                            monthlyParkingTotalCalculations(loc.filteredOrders);

                        const usedSpaces = loc.filteredOrders.reduce((sum, o) => {
                            try {
                                if (!o.line_items_json) return sum;
                                const items = JSON.parse(o.line_items_json);
                                const qty = items.reduce((s, it) => s + Number(it.quantity || 0), 0);
                                return sum + qty;
                            } catch (e) {
                                return sum;
                            }
                        }, 0);

                        return (
                            <React.Fragment key={loc.id}>
                                <tr>
                                    <td
                                        style={{ cursor: "pointer" }}
                                        onClick={() =>
                                            setExpandedLocation(
                                                expandedLocation === loc.id ? null : loc.id
                                            )
                                        }
                                    >
                                        {loc.name}{" "}
                                        <span className="text-muted">
                                            ({loc.filteredOrders.length} Orders)
                                        </span>
                                    </td>
                                    <td>${formatCurrency(totalCash)}</td>
                                    <td>${formatCurrency(totalCheque)}</td>
                                    <td>${formatCurrency(totalStripe)}</td>
                                    <td>${formatCurrency(totalQbo)}</td>
                                    <td>${formatCurrency(totalUnknown)}</td>
                                    <td>${formatCurrency(totalCollected)}</td>
                                    <td>{usedSpaces}</td>
                                    <td>{loc.monthly_parking_capacity}</td>
                                    <td>{loc.monthly_parking_capacity != 0 ? formatPercentage(usedSpaces, loc.monthly_parking_capacity) + "%" : "-"}</td>
                                    <td>
                                        <Button
                                            className={expandedLocation === loc.id ? "warning" : "button-gradient-bg"}
                                            onClick={() =>
                                                setExpandedLocation(
                                                    expandedLocation === loc.id ? null : loc.id
                                                )
                                            }
                                        >
                                            {expandedLocation === loc.id
                                                ? "Hide Orders"
                                                : "View Orders"}
                                        </Button>
                                    </td>
                                    <td>
                                        <ExportButton
                                            data={loc.filteredOrders}
                                            fileName={`${loc.name.replace(/\s+/g, "_")}_Orders`}
                                        />
                                    </td>
                                </tr>

                                {expandedLocation === loc.id && (
                                    <tr>
                                        <td colSpan={8}>
                                            <Table className="align-middle table-sm" responsive>
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>ID</th>
                                                        <th>Date</th>
                                                        <th>Customer</th>
                                                        <th>Items</th>
                                                        <th>Payment Method</th>
                                                        <th>Status</th>
                                                        <th>Total</th>
                                                        {filters.showBarcode && <th>Barcode</th>}
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {loc.filteredOrders
                                                        .sort((a, b) => new Date(b.date_created) - new Date(a.date_created))
                                                        .slice(filters.pagination ? ((locationPages[loc.id] || 1) - 1) * pageSize : 0, filters.pagination ? (locationPages[loc.id] || 1) * pageSize : undefined)
                                                        .map((o) => (
                                                            <tr key={o.id}>
                                                                <td>{o.order_id}</td>
                                                                <CreateOrderTableCells
                                                                    o={o}
                                                                    filters={filters}
                                                                    reloadTime={onRefresh}
                                                                    showDelete={false}
                                                                />
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </Table>

                                            {filters.pagination &&
                                                loc.filteredOrders.length > pageSize && (
                                                    <TablePaginationButtons
                                                        currentPage={locationPages[loc.id] || 1}
                                                        totalItems={loc.filteredOrders.length}
                                                        pageSize={pageSize}
                                                        onPageChange={(newPage) =>
                                                            setLocationPages((prev) => ({
                                                                ...prev,
                                                                [loc.id]: newPage,
                                                            }))
                                                        }
                                                    />
                                                )}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </Table>
        </>
    );
};

export default MonthlyOrdersTableByLocationView;